<template>
  <div
    :style="{ backgroundColor: currentTheme.bg }"
    class="app-shell w-screen overflow-hidden flex justify-center transition-colors duration-700 font-sans"
    :class="currentTheme.text"
  >
      <div
        ref="cardRef"
        class="relative transition-colors duration-500 overflow-hidden flex flex-col"
        :style="{
          width: '100%',
          height: '100%',
          background: currentTheme.cardBg,
          borderColor: currentTheme.border,
          borderRadius: '0px',
          borderLeftWidth: '0px',
          borderRightWidth: '0px',
        }"
      >
        <!-- 子页面（登录/会员/同步/统计等）：web 端收窄并居中为手机比例；
             桌面壳/手机端铺满整窗；主页（计时）始终保持各端原有比例。 -->
        <div
          v-if="currentView !== 'timer'"
          class="w-full h-full flex justify-center overflow-hidden"
        >
          <div
            class="h-full overflow-hidden flex flex-col"
            :class="isWeb ? 'w-full max-w-md border-x shadow-2xl' : 'w-full'"
            :style="isWeb ? { borderColor: currentTheme.border } : {}"
          >
        <login-view
          v-if="currentView === 'login'"
          :theme="currentTheme"
          :t="t"
          :device="device"
          @login="handleLoginSuccess"
          @back="currentView = 'timer'"
        ></login-view>

        <payment-view
          v-else-if="currentView === 'payment'"
          :theme="currentTheme"
          :t="t"
          :device="device"
          @success="handlePaymentSuccess"
          @back="currentView = 'timer'"
        ></payment-view>

        <membership-view
          v-else-if="currentView === 'membership'"
          :theme="currentTheme"
          :t="t"
          :device="device"
          :is-pro="isPro"
          @upgrade="currentView = 'payment'"
          @back="currentView = 'timer'"
        ></membership-view>

        <vip-success-view
          v-else-if="currentView === 'vip_success'"
          :theme="currentTheme"
          :t="t"
          @back="currentView = 'timer'"
        ></vip-success-view>

        <sync-view
          v-else-if="currentView === 'sync'"
          :theme="currentTheme"
          :t="t"
          :is-pro="isPro"
          :record-count="history.length"
          :last-sync-at="lastSyncAt"
          :on-sync="doCloudSync"
          @back="currentView = 'timer'"
        ></sync-view>

        <stats-view
          v-else-if="currentView === 'stats'"
          :theme="currentTheme"
          :t="t"
          :history="parsedHistory"
          :summary="statsSummary"
          :month-heatmap="monthHeatmap"
          :is-same-day="isSameDay"
          @back="currentView = 'timer'"
        ></stats-view>
          </div>
        </div>

        <div v-else class="w-full h-full flex flex-col relative">
          <!-- 跨设备接力横幅：absolute 锚定卡片，自动适配各端宽度（web 居中手机宽 / 桌面·手机铺满） -->
          <transition name="fade">
            <div
              v-if="handoffOffer && !running"
              class="absolute top-[4.5rem] inset-x-3 z-40 flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl backdrop-blur-md"
              :class="
                currentTheme.isDark
                  ? 'bg-neutral-800/90 text-white'
                  : 'bg-white/95 text-neutral-900 border border-black/5'
              "
            >
              <div
                class="w-9 h-9 shrink-0 rounded-full bg-blue-500/15 flex items-center justify-center"
              >
                <icon-base name="Cloud" class="w-5 h-5 text-blue-500"></icon-base>
              </div>
              <div class="min-w-0 flex-1">
                <div class="text-sm font-bold truncate">{{ t.handoff_title }}</div>
                <div class="text-xs opacity-60 truncate">
                  {{ handoffDeviceLabel }} · {{ t.handoff_remaining }}
                  {{ fmt(handoffRemainingSecs) }}
                </div>
              </div>
              <button
                @click="adoptHandoff"
                class="shrink-0 px-3 py-1.5 rounded-xl text-sm font-bold active:scale-95 transition-transform"
                :class="currentTheme.isDark ? 'bg-white text-black' : 'bg-black text-white'"
              >
                {{ t.handoff_continue }}
              </button>
              <button
                @click="handoffOffer = null"
                class="shrink-0 opacity-40 hover:opacity-80 transition-opacity"
              >
                <icon-base name="Close" class="w-5 h-5"></icon-base>
              </button>
            </div>
          </transition>
          <div
            class="shrink-0 h-16 flex items-center justify-between px-6 z-10 relative transition-opacity duration-500"
            :class="running ? 'opacity-30 hover:opacity-100' : ''"
          >
            <div class="flex items-center gap-2">
              <button
                @click="handleProfileClick"
                class="h-9 w-9 grid place-items-center rounded-full transition active:scale-[0.97]"
                :class="[
                  currentTheme.toggle,
                  panels.profile ? 'bg-black/5 dark:bg-white/10' : '',
                  running ? 'pointer-events-none' : '',
                ]"
                :title="t.profile"
              >
                <icon-base name="User" class="w-5 h-5"></icon-base>
              </button>
            </div>
            <div class="flex items-center gap-2">
              <button
                v-if="running"
                @click="bgmOn = !bgmOn"
                class="h-9 w-9 grid place-items-center rounded-full transition active:scale-[0.97]"
                :class="currentTheme.toggle"
              >
                <icon-base name="Sound" class="w-5 h-5"></icon-base>
              </button>
              <button
                v-else
                @click="togglePanel('settings')"
                class="h-9 w-9 grid place-items-center rounded-full transition active:scale-[0.97]"
                :class="[
                  currentTheme.toggle,
                  panels.settings ? 'bg-black/5 dark:bg-white/10' : '',
                ]"
                title="设置"
              >
                <icon-base name="Brain" class="w-5 h-5"></icon-base>
              </button>
            </div>
          </div>

          <div
            class="absolute inset-x-0 top-6 flex justify-center pointer-events-none transition-opacity duration-500"
            :class="running ? 'opacity-20' : 'opacity-50'"
          >
            <div
              class="text-[15px] font-semibold select-none"
              :class="currentTheme.text"
            >
              {{ t.app_name }}
            </div>
          </div>

          <div
            class="grow flex flex-col items-center justify-center text-center px-4 relative z-0 min-h-0 overflow-hidden"
          >
            <div class="flex flex-col items-center justify-center">
              <button
                @click="!running && togglePanel('time')"
                class="select-none font-black leading-none tracking-tight transition-all duration-500"
                :class="!running ? 'hover:opacity-80' : ''"
                :style="timerStyle"
              >
                {{ fmt(seconds) }}
              </button>
              <div class="h-10 mt-2 flex items-center justify-center">
                <button
                  v-if="!running"
                  @click="togglePanel('tag')"
                  class="text-[15px] font-medium tracking-wide select-none transition hover:opacity-80 px-4 py-1.5 rounded-full border border-transparent hover:border-current"
                  :class="currentTheme.sub"
                >
                  {{ tag }}
                </button>
                <div
                  v-else
                  class="text-xs font-medium opacity-60 flex items-center gap-2"
                  :class="
                    currentTheme.isDark
                      ? 'text-emerald-400'
                      : 'text-emerald-600'
                  "
                >
                  <span v-if="protocolEnabled" class="font-bold">
                    {{
                      isProtocolRest
                        ? t.protocol_rest_label
                        : t.protocol_focus_label
                    }}
                    · {{ t.cycle_label }} {{ cycleCount + 1 }}
                    {{ t.cycle_unit }}
                  </span>
                  <span
                    v-if="randomEnabled && !isProtocolRest"
                    class="flex items-center gap-1"
                  >
                    <icon-base name="Zap" class="w-3 h-3"></icon-base>
                    {{ t.neuro_switch }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            class="shrink-0 min-h-[6rem] sm:min-h-[8rem] flex items-center justify-center gap-4 sm:gap-6 pt-2 pb-safe z-10 relative transition-opacity duration-500"
            :class="running ? 'opacity-30 hover:opacity-100' : ''"
          >
            <template v-if="running">
              <button
                @click="pause"
                :class="btnPad"
                class="rounded-full border shadow-sm active:scale-[0.98] transition-opacity bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 text-current"
                title="暂停"
              >
                <icon-base name="Pause" class="h-5 w-5"></icon-base>
              </button>
              <button
                @click="endNow"
                :class="btnPad"
                class="rounded-full border shadow-sm active:scale-[0.98] transition-opacity bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 text-current"
                title="结束"
              >
                <icon-base name="Stop" class="h-5 w-5"></icon-base>
              </button>
            </template>
            <button
              v-else
              @click="start()"
              :disabled="mode === 'down' && seconds === 0"
              class="rounded-full font-semibold transition-all active:scale-[0.98] hover:scale-105"
              :class="[
                device === 'WATCH'
                  ? 'px-8 py-3 text-base'
                  : 'px-12 py-4 text-lg',
                currentTheme.accent,
              ]"
              :style="
                mode === 'down' && seconds === 0
                  ? { opacity: 0.5, cursor: 'not-allowed' }
                  : {}
              "
            >
              {{ t.start_btn }}
            </button>
          </div>

          <div
            v-if="panels.profile && !running"
            :class="pickerClass"
            class="animate-[slideUp_0.3s_ease-out]"
          >
            <div
              class="py-4 text-center font-bold text-lg tracking-tight shrink-0 flex items-center justify-center gap-2"
            >
              <span>{{ t.profile }}</span>
            </div>

            <div class="flex-1 px-6 pb-6 overflow-y-auto min-h-0">
              <button
                @click="!isLoggedIn && goLogin()"
                class="w-full flex items-center gap-4 mb-6 text-left"
                :class="isLoggedIn ? 'cursor-default' : 'active:scale-[0.99] transition-transform'"
              >
                <div
                  class="w-16 h-16 rounded-full bg-gradient-to-tr from-amber-400 to-orange-500 flex items-center justify-center text-white text-2xl shadow-md border-4 border-white dark:border-white/10"
                >
                  <icon-base name="User" class="w-8 h-8"></icon-base>
                </div>
                <div>
                  <div
                    class="text-xl font-bold"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-900'
                    "
                  >
                    {{ isLoggedIn ? t.user_title : t.user_guest }}
                  </div>
                  <div
                    class="text-xs opacity-50 uppercase tracking-wide"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-500'
                    "
                  >
                    {{
                      isPro
                        ? t.user_vip
                        : isLoggedIn
                        ? t.user_level
                        : t.user_login_tip
                    }}
                  </div>
                </div>
              </button>

              <button
                @click="currentView = 'stats'"
                class="w-full grid grid-cols-3 gap-2 mb-6 p-4 rounded-2xl transition-transform active:scale-95"
                :class="
                  currentTheme.isDark
                    ? 'bg-white/5 hover:bg-white/10'
                    : 'bg-black/5 hover:bg-black/10'
                "
              >
                <div class="text-center">
                  <div class="flex justify-center mb-1 text-orange-500">
                    <icon-base name="Activity" class="w-5 h-5"></icon-base>
                  </div>
                  <div
                    class="text-lg font-bold"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-900'
                    "
                  >
                    {{ statsSummary.totalHours }}h
                  </div>
                  <div class="text-[10px] opacity-50">
                    {{ t.stats_duration }}
                  </div>
                </div>
                <div
                  class="text-center border-l border-r border-black/5 dark:border-white/5"
                >
                  <div class="flex justify-center mb-1 text-emerald-500">
                    <icon-base name="Calendar" class="w-5 h-5"></icon-base>
                  </div>
                  <div
                    class="text-lg font-bold"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-900'
                    "
                  >
                    {{ statsSummary.streak }}
                  </div>
                  <div class="text-[10px] opacity-50">
                    {{ t.stats_streak }}
                  </div>
                </div>
                <div class="text-center">
                  <div class="flex justify-center mb-1 text-blue-500">
                    <icon-base name="Award" class="w-5 h-5"></icon-base>
                  </div>
                  <div
                    class="text-lg font-bold"
                    :class="
                      currentTheme.isDark ? 'text-white' : 'text-slate-900'
                    "
                  >
                    {{ statsSummary.totalTasks }}
                  </div>
                  <div class="text-[10px] opacity-50">
                    {{ t.stats_tasks }}
                  </div>
                </div>
              </button>

              <button
                @click="currentView = 'membership'"
                class="w-full h-16 relative overflow-hidden rounded-xl mb-6 shadow-md group"
              >
                <div
                  class="absolute inset-0 z-0"
                  :class="
                    isPro
                      ? 'bg-gradient-to-r from-amber-600 to-amber-800'
                      : 'bg-gradient-to-r from-slate-900 to-slate-800'
                  "
                ></div>
                <div
                  class="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-20 z-0"
                ></div>
                <div
                  class="absolute -right-4 -top-10 w-24 h-24 rounded-full blur-2xl"
                  :class="isPro ? 'bg-white/30' : 'bg-amber-500/30'"
                ></div>
                <div
                  class="relative z-10 flex items-center justify-between px-5 h-full"
                >
                  <div class="flex flex-col items-start">
                    <div
                      class="font-bold text-sm flex items-center gap-1.5"
                      :class="isPro ? 'text-white' : 'text-amber-400'"
                    >
                      <icon-base name="Crown" class="w-4 h-4"></icon-base>
                      <span class="tracking-wide">
                        {{ t.banner_title }} {{ isPro ? t.banner_active : "" }}
                      </span>
                    </div>
                    <div
                      class="text-[10px] mt-0.5"
                      :class="isPro ? 'text-white/70' : 'text-slate-400'"
                    >
                      {{ isPro ? t.banner_desc_vip : t.banner_desc }}
                    </div>
                  </div>
                  <icon-base
                    name="ChevronRight"
                    class="w-5 h-5 transition-colors"
                    :class="
                      isPro
                        ? 'text-white'
                        : 'text-slate-500 group-hover:text-amber-400'
                    "
                  ></icon-base>
                </div>
              </button>

              <div class="space-y-3 mb-6">
                <div
                  class="text-xs font-bold uppercase tracking-wider mb-1 opacity-50 px-2"
                  :class="currentTheme.isDark ? 'text-white' : 'text-black'"
                >
                  {{ t.sec_personal }}
                </div>

                <div
                  class="rounded-xl overflow-hidden transition-all"
                  :class="currentTheme.isDark ? 'bg-white/5' : 'bg-black/5'"
                >
                  <button
                    @click="toggleProfileSection('theme')"
                    class="w-full flex items-center justify-between p-3"
                    :class="
                      currentTheme.isDark
                        ? 'hover:bg-white/5'
                        : 'hover:bg-black/5'
                    "
                  >
                    <div class="flex items-center gap-3">
                      <div
                        class="p-2 rounded-lg bg-purple-500/10 text-purple-500"
                      >
                        <icon-base name="Palette" class="w-4 h-4"></icon-base>
                      </div>
                      <span
                        class="text-sm font-medium"
                        :class="
                          currentTheme.isDark ? 'text-white' : 'text-slate-800'
                        "
                      >
                        {{ t.theme_title }}
                      </span>
                    </div>
                    <icon-base
                      :name="
                        profileSections.theme ? 'ChevronUp' : 'ChevronDown'
                      "
                      class="w-4 h-4 opacity-30"
                    ></icon-base>
                  </button>
                  <div
                    v-if="profileSections.theme"
                    class="p-3 pt-0 animate-[fadeIn_0.2s]"
                  >
                    <!-- 精选：东方五行 + 西方精选 + Claude -->
                    <div class="grid grid-cols-2 gap-2">
                      <button
                        v-for="item in featuredThemes"
                        :key="item.th.id"
                        @click="handleThemeSelect(item.i)"
                        class="flex items-center gap-2.5 p-2 rounded-xl border transition-all text-left relative overflow-hidden"
                        :class="
                          themeIdx === item.i
                            ? currentTheme.isDark
                              ? 'border-white/50 bg-white/10'
                              : 'border-black/25 bg-black/5'
                            : 'border-transparent hover:bg-black/5 dark:hover:bg-white/5'
                        "
                      >
                        <div
                          class="w-9 h-9 rounded-lg shadow-sm border border-black/10 dark:border-white/10 shrink-0 relative"
                          :style="{ background: item.th.cardBg }"
                        >
                          <span
                            v-if="themeIdx === item.i"
                            class="absolute inset-0 flex items-center justify-center"
                          >
                            <icon-base
                              name="Check"
                              class="w-4 h-4"
                              :class="item.th.isDark ? 'text-white' : 'text-black'"
                            ></icon-base>
                          </span>
                        </div>
                        <div class="min-w-0 flex-1">
                          <div
                            class="text-xs font-bold truncate"
                            :class="
                              currentTheme.isDark
                                ? 'text-white'
                                : 'text-neutral-900'
                            "
                          >
                            {{ item.th.name }}
                          </div>
                          <div class="text-[9px] opacity-50 truncate">
                            {{ item.th.meaning }}
                          </div>
                        </div>
                        <icon-base
                          v-if="item.th.isPro && !isPro"
                          name="Lock"
                          class="w-3 h-3 opacity-50 shrink-0"
                        ></icon-base>
                      </button>
                    </div>

                    <!-- 更多主题：默认折叠 -->
                    <button
                      v-if="moreThemes.length"
                      @click="showMoreThemes = !showMoreThemes"
                      class="w-full mt-2 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-medium transition-colors"
                      :class="
                        currentTheme.isDark
                          ? 'bg-white/5 hover:bg-white/10 text-white/70'
                          : 'bg-black/5 hover:bg-black/10 text-neutral-600'
                      "
                    >
                      {{ showMoreThemes ? "收起" : "更多主题" }} ·
                      {{ moreThemes.length }}
                      <icon-base
                        :name="showMoreThemes ? 'ChevronUp' : 'ChevronDown'"
                        class="w-3.5 h-3.5"
                      ></icon-base>
                    </button>
                    <div
                      v-if="showMoreThemes"
                      class="grid grid-cols-2 gap-2 mt-2 animate-[fadeIn_0.2s]"
                    >
                      <button
                        v-for="item in moreThemes"
                        :key="item.th.id"
                        @click="handleThemeSelect(item.i)"
                        class="flex items-center gap-2.5 p-2 rounded-xl border transition-all text-left relative overflow-hidden"
                        :class="
                          themeIdx === item.i
                            ? currentTheme.isDark
                              ? 'border-white/50 bg-white/10'
                              : 'border-black/25 bg-black/5'
                            : 'border-transparent hover:bg-black/5 dark:hover:bg-white/5'
                        "
                      >
                        <div
                          class="w-9 h-9 rounded-lg shadow-sm border border-black/10 dark:border-white/10 shrink-0 relative"
                          :style="{ background: item.th.cardBg }"
                        >
                          <span
                            v-if="themeIdx === item.i"
                            class="absolute inset-0 flex items-center justify-center"
                          >
                            <icon-base
                              name="Check"
                              class="w-4 h-4"
                              :class="item.th.isDark ? 'text-white' : 'text-black'"
                            ></icon-base>
                          </span>
                        </div>
                        <div class="min-w-0 flex-1">
                          <div
                            class="text-xs font-bold truncate"
                            :class="
                              currentTheme.isDark
                                ? 'text-white'
                                : 'text-neutral-900'
                            "
                          >
                            {{ item.th.name }}
                          </div>
                          <div class="text-[9px] opacity-50 truncate">
                            {{ item.th.meaning }}
                          </div>
                        </div>
                        <icon-base
                          v-if="item.th.isPro && !isPro"
                          name="Lock"
                          class="w-3 h-3 opacity-50 shrink-0"
                        ></icon-base>
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  class="rounded-xl overflow-hidden transition-all"
                  :class="currentTheme.isDark ? 'bg-white/5' : 'bg-black/5'"
                >
                  <button
                    @click="toggleProfileSection('sound')"
                    class="w-full flex items-center justify-between p-3"
                    :class="
                      currentTheme.isDark
                        ? 'hover:bg-white/5'
                        : 'hover:bg-black/5'
                    "
                  >
                    <div class="flex items-center gap-3">
                      <div class="p-2 rounded-lg bg-pink-500/10 text-pink-500">
                        <icon-base name="Bell" class="w-4 h-4"></icon-base>
                      </div>
                      <span
                        class="text-sm font-medium"
                        :class="
                          currentTheme.isDark ? 'text-white' : 'text-slate-800'
                        "
                      >
                        {{ t.sound_title }}
                      </span>
                    </div>
                    <icon-base
                      :name="
                        profileSections.sound ? 'ChevronUp' : 'ChevronDown'
                      "
                      class="w-4 h-4 opacity-30"
                    ></icon-base>
                  </button>
                  <div
                    v-if="profileSections.sound"
                    class="p-3 pt-0 grid grid-cols-4 gap-2 animate-[fadeIn_0.2s]"
                  >
                    <button
                      v-for="(s, key) in visibleSounds"
                      :key="key"
                      @click="handleSoundSelect(key)"
                      class="flex flex-col items-center justify-center p-2 rounded-lg transition-all border active:scale-95"
                      :class="
                        sound === key
                          ? currentTheme.isDark
                            ? 'bg-white/10 border-white/50 text-white'
                            : 'bg-black/5 border-black/50 text-black'
                          : 'border-transparent hover:bg-black/5 dark:hover:bg-white/5 text-current opacity-60 hover:opacity-100'
                      "
                    >
                      <div class="relative">
                        <icon-base name="Bell" class="w-5 h-5 mb-1"></icon-base>
                        <div
                          v-if="s.isPro && !isPro"
                          class="absolute -top-1 -right-1 bg-amber-500 rounded-full w-2 h-2"
                        ></div>
                      </div>
                      <span class="text-[10px] font-bold">
                        {{ s.name[lang] || s.name }}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="space-y-1">
                <div
                  class="text-xs font-bold uppercase tracking-wider mb-2 opacity-50 px-2"
                  :class="currentTheme.isDark ? 'text-white' : 'text-black'"
                >
                  {{ t.sec_general }}
                </div>
                <div
                  class="w-full flex items-center justify-between p-3 rounded-xl transition-colors"
                  :class="currentTheme.isDark ? 'bg-white/5' : 'bg-black/5'"
                >
                  <div class="flex items-center gap-3">
                    <div class="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                      <icon-base name="Globe" class="w-4 h-4"></icon-base>
                    </div>
                    <span
                      class="text-sm font-medium"
                      :class="
                        currentTheme.isDark ? 'text-white' : 'text-slate-800'
                      "
                    >
                      {{ t.lang_title }}
                    </span>
                  </div>
                  <div class="flex gap-1">
                    <button
                      v-for="l in ['zh-CN', 'en', 'zh-TW']"
                      :key="l"
                      @click="lang = l"
                      class="text-[10px] px-2 py-1 rounded-md transition-colors"
                      :class="
                        lang === l
                          ? currentTheme.isDark
                            ? 'bg-white text-black'
                            : 'bg-black text-white'
                          : 'opacity-50 hover:opacity-100'
                      "
                    >
                      {{ l === "zh-CN" ? "简" : l === "en" ? "EN" : "繁" }}
                    </button>
                  </div>
                </div>
                <button
                  @click="vibrationOn = !vibrationOn"
                  class="w-full flex items-center justify-between p-3 rounded-xl transition-colors active:bg-black/5 dark:active:bg-white/10"
                  :class="
                    currentTheme.isDark
                      ? 'hover:bg-white/5'
                      : 'hover:bg-black/5'
                  "
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="p-2 rounded-lg"
                      :class="
                        vibrationOn
                          ? 'bg-emerald-500/10 text-emerald-500'
                          : 'bg-gray-500/10 text-gray-500'
                      "
                    >
                      <icon-base name="Zap" class="w-4 h-4"></icon-base>
                    </div>
                    <span
                      class="text-sm font-medium"
                      :class="
                        currentTheme.isDark ? 'text-white' : 'text-slate-800'
                      "
                    >
                      {{ t.haptic_title }}
                    </span>
                  </div>
                  <div
                    class="w-10 h-6 rounded-full p-1 transition-colors"
                    :class="
                      vibrationOn
                        ? 'bg-emerald-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                    "
                  >
                    <div
                      class="w-4 h-4 bg-white rounded-full shadow-sm transition-transform"
                      :class="vibrationOn ? 'translate-x-4' : 'translate-x-0'"
                    ></div>
                  </div>
                </button>
                <button
                  @click="handleSyncClick"
                  class="w-full flex items-center justify-between p-3 rounded-xl transition-colors active:bg-black/5 dark:active:bg-white/10"
                  :class="
                    currentTheme.isDark
                      ? 'hover:bg-white/5'
                      : 'hover:bg-black/5'
                  "
                >
                  <div class="flex items-center gap-3">
                    <div
                      class="p-2 rounded-lg bg-orange-500/10 text-orange-500"
                    >
                      <icon-base name="Refresh" class="w-4 h-4"></icon-base>
                    </div>
                    <span
                      class="text-sm font-medium"
                      :class="
                        currentTheme.isDark ? 'text-white' : 'text-slate-800'
                      "
                    >
                      {{ t.sync_title }}
                    </span>
                  </div>
                  <icon-base
                    v-if="!isPro"
                    name="Lock"
                    class="w-3 h-3 opacity-50"
                  ></icon-base>
                  <icon-base
                    v-else
                    name="ChevronRight"
                    class="w-4 h-4 opacity-30"
                  ></icon-base>
                </button>
              </div>
            </div>

            <div class="px-4 pb-4 mt-auto">
              <button
                @click="closeAllPanels"
                :class="pickerBtnClass"
                class="w-full py-3.5 rounded-xl font-bold"
              >
                {{ t.close_btn }}
              </button>

              <!-- 退出登录降级为次要文字按钮，与「关闭」明显区分，避免误点 -->
              <button
                v-if="isLoggedIn"
                @click="handleLogout"
                class="block mx-auto mt-3 px-4 py-1.5 text-xs font-medium text-red-500/70 hover:text-red-500 transition-colors"
              >
                {{ lang === "en" ? "Log Out" : "退出登录" }}
              </button>
            </div>
          </div>

          <div
            v-if="panels.settings && !running"
            :class="pickerClass"
            class="animate-[slideUp_0.3s_ease-out]"
          >
            <div
              class="py-4 text-center font-bold text-lg tracking-tight shrink-0"
            >
              {{ t.settings_neuro }}
            </div>
            <div class="px-6 pb-6 overflow-y-auto space-y-4">
              <div
                class="p-4 rounded-2xl border"
                :class="
                  currentTheme.isDark
                    ? 'bg-white/5 border-white/10'
                    : 'bg-black/5 border-black/5'
                "
              >
                <div class="font-bold mb-3">{{ t.profile_title }}</div>
                <div class="space-y-2">
                  <button
                    v-for="p in ['memorize', 'skill', 'deep']"
                    :key="p"
                    @click="applyProfile(p)"
                    class="w-full text-left p-3 rounded-xl border transition"
                    :class="
                      focusProfile === p
                        ? 'border-emerald-500 bg-emerald-500/10'
                        : currentTheme.isDark
                        ? 'border-transparent bg-white/5 hover:bg-white/10'
                        : 'border-transparent bg-black/5 hover:bg-black/10'
                    "
                  >
                    <div class="text-sm font-bold">
                      {{ t["profile_" + p] }}
                    </div>
                    <div class="text-xs opacity-60 mt-0.5">
                      {{ t["profile_" + p + "_d"] }}
                    </div>
                  </button>
                </div>
              </div>

              <div
                class="p-4 rounded-2xl border"
                :class="
                  currentTheme.isDark
                    ? 'bg-white/5 border-white/10'
                    : 'bg-black/5 border-black/5'
                "
              >
                <div class="flex items-center justify-between relative">
                  <span class="font-bold flex items-center gap-2">{{
                    t.neuro_switch
                  }}</span>
                  <button
                    @click="toggleRandom"
                    class="w-12 h-7 rounded-full p-1 transition-colors duration-300"
                    :class="randomEnabled ? 'bg-emerald-500' : 'bg-gray-300'"
                  >
                    <div
                      class="w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300"
                      :class="randomEnabled ? 'translate-x-5' : 'translate-x-0'"
                    ></div>
                  </button>
                </div>
                <div v-if="randomEnabled" class="animate-[fadeIn_0.2s] mt-4">
                  <div class="text-xs opacity-60 mb-2">{{ t.neuro_range }}</div>
                  <div class="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      v-model.number="randomMin"
                      class="p-2 w-16 text-center text-black rounded-lg"
                    />
                    -
                    <input
                      type="number"
                      min="1"
                      v-model.number="randomMax"
                      class="p-2 w-16 text-center text-black rounded-lg"
                    />
                  </div>
                  <div class="text-[10px] opacity-50 mt-2">
                    {{ t.neuro_tip }}
                  </div>
                </div>
              </div>

              <div
                class="p-4 rounded-2xl border"
                :class="
                  currentTheme.isDark
                    ? 'bg-white/5 border-white/10'
                    : 'bg-black/5 border-black/5'
                "
              >
                <div class="flex items-center justify-between relative">
                  <div>
                    <div class="font-bold">{{ t.protocol_title }}</div>
                    <div class="text-xs opacity-60 mt-0.5">
                      {{ t.protocol_desc }}
                    </div>
                  </div>
                  <button
                    @click="protocolEnabled = !protocolEnabled"
                    class="w-12 h-7 rounded-full p-1 transition-colors duration-300 shrink-0"
                    :class="protocolEnabled ? 'bg-emerald-500' : 'bg-gray-300'"
                  >
                    <div
                      class="w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-300"
                      :class="
                        protocolEnabled ? 'translate-x-5' : 'translate-x-0'
                      "
                    ></div>
                  </button>
                </div>
                <div
                  v-if="protocolEnabled"
                  class="animate-[fadeIn_0.2s] mt-4 flex items-center gap-4"
                >
                  <label class="flex items-center gap-2 text-xs opacity-80">
                    {{ t.protocol_focus_min }}
                    <input
                      type="number"
                      min="1"
                      v-model.number="protocolFocusMin"
                      class="p-2 w-16 text-center text-black rounded-lg"
                    />
                  </label>
                  <label class="flex items-center gap-2 text-xs opacity-80">
                    {{ t.protocol_rest_min }}
                    <input
                      type="number"
                      min="1"
                      v-model.number="protocolRestMin"
                      class="p-2 w-16 text-center text-black rounded-lg"
                    />
                  </label>
                </div>
              </div>
            </div>
            <div class="px-4 pb-4 mt-auto">
              <button
                @click="closeAllPanels"
                :class="pickerBtnClass"
                class="w-full py-3.5 rounded-xl font-bold"
              >
                {{ t.done_btn }}
              </button>
            </div>
          </div>

          <div
            v-if="panels.time && !running"
            :class="pickerClass"
            class="animate-[slideUp_0.3s_ease-out]"
          >
            <div class="grid grid-cols-3 gap-3 px-4 pb-6 pt-6 overflow-y-auto">
              <button
                v-for="c in timeChoices"
                :key="c.t"
                @click="applySeconds(c.v)"
                :class="pickerBtnClass"
                class="rounded-xl py-3 font-semibold shadow-sm transition active:scale-95"
              >
                {{ c.t }}
              </button>
            </div>
            <div class="px-4 pb-4 grid grid-cols-2 gap-3 mt-auto">
              <button
                @click="pickUnlimited"
                :class="pickerBtnClass"
                class="rounded-xl py-3 font-semibold"
              >
                ∞
              </button>
              <button
                @click="openCustomTime"
                :class="pickerBtnClass"
                class="rounded-xl py-3 font-semibold"
              >
                ...
              </button>
            </div>
            <div v-if="showCustomTime" class="px-4 pb-4 animate-[fadeIn_0.2s]">
              <div
                class="flex items-center gap-3 bg-black/5 dark:bg-white/5 p-2 rounded-xl"
              >
                <input
                  type="number"
                  v-model="customMin"
                  class="w-16 text-center text-black"
                />
                :
                <input
                  type="number"
                  v-model="customSec"
                  class="w-16 text-center text-black"
                />
                <button
                  @click="applyCustomTime"
                  class="ml-auto rounded-lg px-4 py-2 text-sm font-bold bg-black text-white"
                >
                  GO
                </button>
              </div>
            </div>
            <div class="px-4 pb-4">
              <button
                @click="closeAllPanels"
                :class="pickerBtnClass"
                class="w-full py-3.5 rounded-xl font-bold"
              >
                {{ t.close_btn }}
              </button>
            </div>
          </div>

          <div
            v-if="panels.tag && !running"
            :class="pickerClass"
            class="animate-[slideUp_0.3s_ease-out]"
          >
            <div class="grid grid-cols-3 gap-3 px-4 pb-4 pt-6 overflow-y-auto">
              <div v-for="tg in tags" :key="tg" class="relative">
                <button
                  @click="applyTagChoice(tg)"
                  :class="pickerBtnClass"
                  class="w-full rounded-xl py-2.5 font-semibold text-sm"
                >
                  {{ tg }}
                </button>
                <!-- 自定义标签可删除；内置标签不显示删除按钮 -->
                <button
                  v-if="customTags.includes(tg)"
                  @click.stop="removeCustomTag(tg)"
                  class="absolute -top-1.5 -right-1.5 w-5 h-5 grid place-items-center rounded-full bg-red-500 text-white shadow-sm active:scale-90 transition-transform"
                  :title="lang === 'en' ? 'Delete' : '删除'"
                >
                  <icon-base name="Close" class="w-3 h-3"></icon-base>
                </button>
              </div>
            </div>
            <div class="px-4 pb-4 mt-auto flex items-center gap-2">
              <input
                v-model="customTagInput"
                placeholder="+"
                class="flex-1 rounded-xl px-4 py-3 bg-black/5 text-black"
              />
              <button
                @click="addCustomTag"
                class="rounded-xl px-5 py-3 font-bold bg-black text-white"
              >
                ＋
              </button>
            </div>
            <div class="px-4 pb-4">
              <button
                @click="closeAllPanels"
                :class="pickerBtnClass"
                class="w-full py-3.5 rounded-xl font-bold"
              >
                {{ t.close_btn }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <transition name="fade">
        <div
          v-if="microRest.active"
          @click="endMicroRest"
          class="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center cursor-pointer select-none"
        >
          <div class="relative flex items-center justify-center mb-10">
            <div
              class="absolute w-44 h-44 rounded-full bg-emerald-500/15 animate-[microBreathe_4s_ease-in-out_infinite]"
            ></div>
            <div
              class="absolute w-32 h-32 rounded-full bg-emerald-500/20 animate-[microBreathe_4s_ease-in-out_infinite]"
              style="animation-delay: 0.4s"
            ></div>
            <div
              class="w-24 h-24 rounded-full bg-emerald-500/30 flex items-center justify-center"
            >
              <span class="text-5xl font-black text-white tabular-nums">{{
                microRest.count
              }}</span>
            </div>
          </div>
          <div class="text-xl font-bold text-white mb-1">
            {{ t.neuro_toast_title }}
          </div>
          <div class="text-sm text-white/60">{{ t.neuro_toast_desc }}</div>
          <div class="text-xs text-white/40 mt-8">{{ t.micro_rest_skip }}</div>
        </div>
      </transition>

      <transition name="fade">
        <div
          v-if="showCongrats"
          @click="showCongrats = false"
          class="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-3 cursor-pointer"
        >
          <!-- <icon-base name="Check" class="w-5 h-5"></icon-base> -->
          <span class="font-bold">{{ congratsText }}</span>
        </div>
      </transition>

      <!-- 一轮专注结束的完成引导：休息一下 / 再来一轮 / 返回 -->
      <transition name="fade">
        <div
          v-if="sessionComplete"
          class="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center px-8 select-none"
        >
          <div
            class="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6"
          >
            <icon-base name="Check" class="w-10 h-10 text-emerald-400"></icon-base>
          </div>
          <div class="text-2xl font-black text-white mb-2 text-center">
            {{ t.congrats_title }}
          </div>
          <div class="h-6 mb-8 text-sm text-white/50">
            <span v-if="completedMinutes > 0">
              {{ t.session_done_prefix }} {{ completedMinutes }}
              {{ t.session_done_unit }}
            </span>
          </div>
          <div class="w-full max-w-xs flex flex-col gap-3">
            <button
              @click="startAgain"
              class="w-full py-3.5 rounded-2xl font-bold bg-white text-black active:scale-95 transition-transform"
            >
              {{ t.session_again }}
            </button>
            <button
              @click="restAfterComplete"
              class="w-full py-3.5 rounded-2xl font-bold bg-emerald-500/20 text-emerald-300 active:scale-95 transition-transform"
            >
              {{ t.session_rest }}
            </button>
            <button
              @click="$refs.completeShare.open()"
              class="w-full py-3.5 rounded-2xl font-bold bg-white/10 text-white/90 active:scale-95 transition-transform flex items-center justify-center gap-2"
            >
              <icon-base name="Share" class="w-4 h-4"></icon-base>
              {{ t.session_share }}
            </button>
            <button
              @click="dismissComplete"
              class="w-full py-2.5 text-sm font-medium text-white/40 hover:text-white/70 transition-colors"
            >
              {{ t.session_back }}
            </button>
          </div>
          <!-- 计时完成页直接分享成绩单：浮在完成层之上，关掉后回到「再来一轮/休息」 -->
          <share-card
            ref="completeShare"
            :t="t"
            :history="parsedHistory"
            :summary="statsSummary"
            :month-heatmap="monthHeatmap"
            :is-same-day="isSameDay"
          ></share-card>
        </div>
      </transition>

      <!-- ============ 开发测试面板 ============
           仅本地开发（npm run serve）可见；生产构建（npm run build）时
           process.env.NODE_ENV 为 'production'，isDev=false，整块不渲染，正式发布自动去掉。 -->
      <div v-if="isDev" class="fixed left-3 bottom-3 z-[60] select-none">
        <button
          v-if="!devPanelOpen"
          @click="devPanelOpen = true"
          class="w-11 h-11 rounded-full bg-black/70 text-white text-xl shadow-lg backdrop-blur flex items-center justify-center active:scale-95 transition-transform"
          title="测试面板（仅开发可见）"
        >
          🧪
        </button>
        <div
          v-else
          class="w-56 rounded-2xl bg-neutral-900/90 text-white text-xs backdrop-blur shadow-2xl p-3 space-y-2 border border-white/10"
        >
          <div class="flex items-center justify-between mb-1">
            <span class="font-bold tracking-wide">🧪 测试面板</span>
            <button
              @click="devPanelOpen = false"
              class="opacity-60 hover:opacity-100 px-1"
            >
              ✕
            </button>
          </div>
          <button
            @click="isPro = !isPro"
            class="w-full flex items-center justify-between px-2.5 py-2 rounded-lg transition-colors"
            :class="isPro ? 'bg-emerald-500/30 text-emerald-200' : 'bg-white/10'"
          >
            <span>Pro 会员（解锁全部主题/音效/同步）</span>
            <span class="font-bold">{{ isPro ? "开" : "关" }}</span>
          </button>
          <button
            @click="isLoggedIn = !isLoggedIn"
            class="w-full flex items-center justify-between px-2.5 py-2 rounded-lg transition-colors"
            :class="isLoggedIn ? 'bg-blue-500/30 text-blue-200' : 'bg-white/10'"
          >
            <span>登录态</span>
            <span class="font-bold">{{ isLoggedIn ? "已登录" : "未登录" }}</span>
          </button>
          <button
            @click="devReset"
            class="w-full text-left px-2.5 py-2 rounded-lg bg-red-500/15 text-red-300 hover:bg-red-500/25 transition-colors"
          >
            清空本地数据并刷新
          </button>
          <div class="opacity-40 text-[10px] leading-tight pt-0.5">
            仅本地开发可见 · 正式发布自动移除
          </div>
        </div>
      </div>

  </div>
</template>

<script>
import {
  TRANSLATIONS,
  SOUNDS,
  THEMES,
  TAG_MAP,
  REST_TAGS,
  PROFILES,
  FEATURED_THEME_IDS,
} from "../constants/data";
import LoginView from "../components/LoginView.vue";
import PaymentView from "../components/PaymentView.vue";
import MembershipView from "../components/MembershipView.vue";
import VipSuccessView from "../components/VipSuccessView.vue";
import SyncView from "../components/SyncView.vue";
import StatsView from "../components/StatsView.vue";
import ShareCard from "../components/ShareCard.vue";
import {
  startFocus,
  pingFocus,
  finishFocus,
  getUserInfo,
  getActiveFocus,
  publishActiveFocus,
  clearActiveFocus,
  syncData,
} from "@/api";
import {
  playChime,
  unlockAudio,
  startAmbient,
  stopAmbient,
} from "@/utils/sounds";
import { Handoff } from "@/utils/handoff";
import { getPlatform } from "@/utils/platform";
import { setTrayTitle, nativeNotify, ensureNotifyPermission } from "@/utils/native";
import { AUTH_EXPIRED_EVENT } from "@/utils/request";

// 计时数字按真实字体测量宽度，让字号自适应可用空间——各端等比例、永不溢出。
// 返回「文本宽度 / 字号」的比值（与字号无关），用一张离屏 canvas 复用测量。
const _measureCanvas =
  typeof document !== "undefined" ? document.createElement("canvas") : null;
const _measureCtx = _measureCanvas ? _measureCanvas.getContext("2d") : null;
const _advanceCache = Object.create(null);
function timerTextAdvance(text, fontFamily) {
  if (!_measureCtx) return text.length * 0.6; // SSR/无 canvas 兜底
  const key = `${text}|${fontFamily}`;
  if (_advanceCache[key] != null) return _advanceCache[key];
  // 计时数字用 font-black（900 字重），与模板一致才能测得准
  _measureCtx.font = `900 100px ${fontFamily}`;
  const advance = _measureCtx.measureText(text).width / 100;
  _advanceCache[key] = advance;
  return advance;
}

export default {
  name: "ZenoApp",
  components: {
    LoginView,
    PaymentView,
    MembershipView,
    VipSuccessView,
    SyncView,
    StatsView,
    ShareCard,
  },
  data() {
    return {
      // 开发测试面板：仅本地开发（serve）为 true；生产构建时 NODE_ENV='production'，
      // 被 webpack 定值为 false，测试面板整块不渲染 → 正式发布自动去掉。
      isDev: process.env.NODE_ENV === "development",
      devPanelOpen: false,
      // 持久化状态
      isLoggedIn: JSON.parse(localStorage.getItem("zeno_isLoggedIn")) || false,
      isPro: JSON.parse(localStorage.getItem("zeno_isPro")) || false,
      themeIdx: JSON.parse(localStorage.getItem("zeno_themeIdx")) || 0,
      // 旧版本可能存了已移除的音效键（如 nature/rain），回退到新默认音「清铃」
      sound: SOUNDS[JSON.parse(localStorage.getItem("zeno_sound"))]
        ? JSON.parse(localStorage.getItem("zeno_sound"))
        : "chime",
      history: JSON.parse(localStorage.getItem("zeno_history")) || [],
      lastSyncAt: JSON.parse(localStorage.getItem("zeno_lastSyncAt")) || null,
      // 心流提示音是方法核心，默认开启；仅当用户显式存过 false 时才关闭
      randomEnabled:
        localStorage.getItem("zeno_randomEnabled") === null
          ? true
          : JSON.parse(localStorage.getItem("zeno_randomEnabled")),
      lang: JSON.parse(localStorage.getItem("zeno_lang")) || "zh-CN",
      randomMin: JSON.parse(localStorage.getItem("zeno_randomMin")) || 3,
      randomMax: JSON.parse(localStorage.getItem("zeno_randomMax")) || 5,
      focusProfile:
        JSON.parse(localStorage.getItem("zeno_focusProfile")) || "memorize",
      protocolEnabled:
        JSON.parse(localStorage.getItem("zeno_protocolEnabled")) || false,
      protocolFocusMin:
        JSON.parse(localStorage.getItem("zeno_protocolFocusMin")) || 90,
      protocolRestMin:
        JSON.parse(localStorage.getItem("zeno_protocolRestMin")) || 20,

      // 临时状态
      // 运行平台：'desktop'(Tauri 桌面壳) | 'mobile' | 'web'，开机时探测一次。
      platform: getPlatform(),
      // device 仅作为子组件的内部布局语义沿用；各端统一走紧凑单列「小组件」布局。
      device: "IPHONE",
      seconds: 25 * 60,
      timerState: "idle",
      currentView: "timer",
      panels: {
        time: false,
        tag: false,
        theme: false,
        sound: false,
        settings: false,
        profile: false,
      },

      tag: "",
      tags: [],
      customTags: JSON.parse(localStorage.getItem("zeno_customTags")) || [],
      mode: "down",
      bgmOn: false,
      nextRandomTrigger: null,
      microRest: { active: false, count: 10 },
      protocolPhase: "focus",
      cycleCount: 0,
      vibrationOn:
        localStorage.getItem("zeno_vibrationOn") === null
          ? true
          : JSON.parse(localStorage.getItem("zeno_vibrationOn")),
      showCongrats: false,
      congratsText: "",
      // 一轮专注结束后的「完成引导」全屏层：让用户选择休息/再来一轮，避免停在 0:00 无反应
      sessionComplete: false,
      completedMinutes: 0,
      containerDim: { w: 300, h: 600 },
      // 计时数字实际生效的字体族（挂载后从 DOM 读真值，用于精确测量字号）。
      timerFontFamily:
        'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      profileSections: { theme: false, sound: false },
      // 主题选择器「更多」区是否展开
      showMoreThemes: false,

      initialTime: 25 * 60,
      pausedTime: null,
      intervalId: null,

      customMin: 25,
      customSec: 0,
      // 用户是否在本轮显式选过时长：选过则 start 时尊重该选择，不被 90/20 协议默认时长覆盖
      userPickedDuration: false,
      showCustomTime: false,
      customTagInput: "",

      sounds: SOUNDS,
      themes: THEMES,
      timeChoices: [
        { t: "15:00", v: 900 },
        { t: "25:00", v: 1500 },
        { t: "30:00", v: 1800 },
        { t: "45:00", v: 2700 },
        { t: "60:00", v: 3600 },
        { t: "90:00", v: 5400 },
      ],
      currentSessionId: null, // 新增：用于存储当前专注的 session_id
      pingInterval: null, // 新增：用于心跳上报的定时器
      // —— 跨设备接力（Handoff）——
      handoff: null, // Handoff 传输实例
      handoffOffer: null, // 探测到的「他设备正在专注」快照，非空即展示接力横幅
      handoffClock: 0, // 心跳计数，驱动横幅剩余时间每秒刷新
      handoffPublishTimer: null, // 本设备专注中周期广播快照的定时器
      handoffWatchTimer: null, // 过期检测：他设备停止广播后撤掉横幅
      // 注：_liveSessionId 作为非响应式实例字段直接挂在 this 上（Vue 2 保留 _ 开头的 data key）
    };
  },
  computed: {
    t() {
      return TRANSLATIONS[this.lang];
    },
    currentTheme() {
      return THEMES[this.themeIdx];
    },
    // web 端居中为手机宽度；桌面壳与手机端铺满整窗（小组件形态）。
    isWeb() {
      return this.platform === "web";
    },
    running() {
      return this.timerState === "running";
    },
    timerStyle() {
      return {
        fontFeatureSettings: '"tnum" on',
        fontSize: this.timeSize,
        opacity: this.running ? 0.9 : 1,
      };
    },
    pickerClass() {
      return `${
        this.currentTheme.isDark
          ? "bg-neutral-900 text-white border-neutral-700"
          : "bg-white text-neutral-900 border-neutral-200"
      } mx-auto w-full max-w-md rounded-t-3xl border-t shadow-2xl safe-bottom absolute bottom-0 inset-x-0 z-20 flex flex-col max-h-[85%]`;
    },
    pickerBtnClass() {
      return `${
        this.currentTheme.isDark
          ? "bg-neutral-800 hover:bg-neutral-700"
          : "bg-neutral-100 hover:bg-neutral-200"
      }`;
    },
    btnPad() {
      return this.device === "WATCH"
        ? "px-4 py-2 text-sm"
        : "px-6 py-3 text-base";
    },
    // 时钟字号随当前卡片实际尺寸 + 屏幕宽高比自适应，保持「一致的视觉比例」，不会太大。
    // 关键：越窄高的屏（手机），横向占比越收紧——否则窄屏会把数字撑得相对整屏过大；
    // 越方/越宽的屏放宽占比。横向按真实测量宽度反推字号，纵向占可用高度一定比例，两者取小。
    timeSize() {
      const { w, h } = this.containerDim;
      if (!w || !h) return "96px";
      const availableH = Math.max(100, h - 220);
      const availableW = w - 40;
      const advance = timerTextAdvance(this.fmt(this.seconds), this.timerFontFamily);

      // 宽高比：手机≈0.46（窄高），方屏≈1。据此把横向占比夹在 0.62–0.82 之间，
      // 窄高屏取低值（数字不被撑大），方/宽屏取高值，实现按屏幕比例的等比缩放。
      const ratio = w / h;
      const widthFrac = Math.max(0.62, Math.min(0.82, 0.5 + ratio * 0.34));

      // advance 为 0 时退回旧的经验系数兜底。
      const widthBound =
        advance > 0 ? (availableW * widthFrac) / advance : availableW * 0.4;
      const heightBound = availableH * 0.44;
      let fontSize = Math.min(widthBound, heightBound);
      // 网页端主页铺满整屏，放宽上限到 200px 让数字撑起空间；手机/桌面壳维持 132px 不过大。
      const maxFont = this.isWeb ? 200 : 132;
      fontSize = Math.max(44, Math.min(fontSize, maxFont));
      if (this.running) fontSize *= 0.95;
      return `${Math.round(fontSize)}px`;
    },
    parsedHistory() {
      return this.history
        .map((h) => ({ ...h, date: new Date(h.date) }))
        .sort((a, b) => b.date - a.date);
    },
    statsSummary() {
      const totalMins = this.parsedHistory.reduce(
        (acc, cur) => acc + cur.duration,
        0
      );
      const totalHours = Math.floor(totalMins / 60);
      let streak = 0;
      const today = new Date();
      const uniqueDates = [
        ...new Set(this.parsedHistory.map((h) => h.date.toDateString())),
      ]
        .map((d) => new Date(d))
        .sort((a, b) => b - a);
      if (
        uniqueDates.length > 0 &&
        (this.isSameDay(uniqueDates[0], today) ||
          this.isSameDay(uniqueDates[0], new Date(today.getTime() - 86400000)))
      ) {
        streak = 1;
        for (let i = 0; i < uniqueDates.length - 1; i++) {
          if (
            Math.round((uniqueDates[i] - uniqueDates[i + 1]) / 86400000) === 1
          )
            streak++;
          else break;
        }
      }
      return { totalHours, totalTasks: this.parsedHistory.length, streak };
    },
    monthHeatmap() {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const days = [];
      for (let i = 1; i <= daysInMonth; i++) {
        const currentDate = new Date(year, month, i);
        const dayRecords = this.parsedHistory.filter((h) =>
          this.isSameDay(h.date, currentDate)
        );
        // minutes：当日心流（专注）总时长，用于热力图按「时间多久」着色
        const minutes = dayRecords.reduce((acc, h) => acc + h.duration, 0);
        days.push({
          date: currentDate,
          tasksCount: dayRecords.length,
          minutes,
          dayNum: i,
          isToday: this.isSameDay(currentDate, today),
        });
      }
      return days;
    },
    visibleSounds() {
      const filtered = {};
      Object.keys(this.sounds).forEach((key) => {
        if (key !== "ping") {
          filtered[key] = this.sounds[key];
        }
      });
      return filtered;
    },
    // 主题选择器：{th, i} 保留 THEMES 原始索引 i（handleThemeSelect / themeIdx 依赖它）。
    // 精选按 FEATURED_THEME_IDS 的顺序排列（东方五行→西方精选→Claude）。
    featuredThemes() {
      return FEATURED_THEME_IDS.map((id) => {
        const i = this.themes.findIndex((t) => t.id === id);
        return i >= 0 ? { th: this.themes[i], i } : null;
      }).filter(Boolean);
    },
    moreThemes() {
      return this.themes
        .map((th, i) => ({ th, i }))
        .filter((x) => !FEATURED_THEME_IDS.includes(x.th.id));
    },
    isProtocolRest() {
      return this.protocolEnabled && this.protocolPhase === "rest";
    },
    randomActive() {
      return this.randomEnabled && !this.isProtocolRest;
    },
    // 接力横幅上的「剩余时间」。依赖 handoffClock，使其随心跳每秒刷新。
    handoffRemainingSecs() {
      const snap = this.handoffOffer;
      if (!snap) return 0;
      this.handoffClock; // 建立响应式依赖
      const now = Date.now();
      if (snap.mode === "up") {
        return snap.startTime
          ? Math.max(0, Math.floor((now - snap.startTime) / 1000))
          : snap.seconds || 0;
      }
      return snap.endTime
        ? Math.max(0, Math.ceil((snap.endTime - now) / 1000))
        : snap.seconds || 0;
    },
    // 把快照里的平台 key 本地化成「桌面端/手机端/网页端」。
    handoffDeviceLabel() {
      const snap = this.handoffOffer;
      if (!snap) return "";
      return this.t[`handoff_dev_${snap.platform}`] || this.t.handoff_dev_web;
    },
  },
  watch: {
    isLoggedIn(v) {
      localStorage.setItem("zeno_isLoggedIn", JSON.stringify(v));
    },
    isPro(v) {
      localStorage.setItem("zeno_isPro", JSON.stringify(v));
    },
    themeIdx(v) {
      localStorage.setItem("zeno_themeIdx", JSON.stringify(v));
    },
    sound(v) {
      localStorage.setItem("zeno_sound", JSON.stringify(v));
    },
    history: {
      handler(v) {
        localStorage.setItem("zeno_history", JSON.stringify(v));
      },
      deep: true,
    },
    lastSyncAt(v) {
      localStorage.setItem("zeno_lastSyncAt", JSON.stringify(v));
    },
    randomEnabled(v) {
      localStorage.setItem("zeno_randomEnabled", JSON.stringify(v));
      // 运行中切换心流提示音时，timerState watcher 已经触发过、不会再排程，
      // 必须在这里补排/清除，否则中途打开的开关整段会话内不响（无限计时下尤其明显）。
      if (this.timerState !== "running") return;
      if (v) {
        if (this.randomActive && !this.nextRandomTrigger) {
          this.scheduleNextRandom(this.mode === "down" ? this.seconds : null);
        }
      } else {
        this.nextRandomTrigger = null;
        this._randomRemainingMs = null;
      }
    },
    lang(v) {
      localStorage.setItem("zeno_lang", JSON.stringify(v));
      this.initTags();
    },
    // 分钟类设置一律取整并下限 1：这些值随接力快照上报到后端（DTO 为 int），
    // 小数会被拒收并触发接力通道熔断；输入清空时暂不持久化，等输入完成。
    randomMin(v) {
      if (v === "" || v == null) return;
      const n = Math.max(1, Math.round(v));
      if (n !== v) {
        this.randomMin = n;
        return;
      }
      localStorage.setItem("zeno_randomMin", JSON.stringify(v));
    },
    randomMax(v) {
      if (v === "" || v == null) return;
      const n = Math.max(1, Math.round(v));
      if (n !== v) {
        this.randomMax = n;
        return;
      }
      localStorage.setItem("zeno_randomMax", JSON.stringify(v));
    },
    focusProfile(v) {
      localStorage.setItem("zeno_focusProfile", JSON.stringify(v));
    },
    protocolEnabled(v) {
      localStorage.setItem("zeno_protocolEnabled", JSON.stringify(v));
    },
    protocolFocusMin(v) {
      if (v === "" || v == null) return;
      const n = Math.max(1, Math.round(v));
      if (n !== v) {
        this.protocolFocusMin = n;
        return;
      }
      localStorage.setItem("zeno_protocolFocusMin", JSON.stringify(v));
    },
    protocolRestMin(v) {
      if (v === "" || v == null) return;
      const n = Math.max(1, Math.round(v));
      if (n !== v) {
        this.protocolRestMin = n;
        return;
      }
      localStorage.setItem("zeno_protocolRestMin", JSON.stringify(v));
    },
    // 自定义时长面板展开时，输入即生效：边输入边同步到 seconds，
    // 避免用户输入 5 分钟后没点 GO 直接开始、仍按旧的 25 分钟跑
    customMin() {
      if (this.showCustomTime) this.syncCustomTime();
    },
    customSec() {
      if (this.showCustomTime) this.syncCustomTime();
    },
    vibrationOn(v) {
      localStorage.setItem("zeno_vibrationOn", JSON.stringify(v));
    },
    customTags: {
      handler(v) {
        localStorage.setItem("zeno_customTags", JSON.stringify(v));
      },
      deep: true,
    },
    bgmOn(v) {
      // 背景环境音改为本地合成的柔和白噪音，无需联网、不依赖外链。
      if (v) startAmbient(0.15);
      else stopAmbient();
    },
    timerState(newState) {
      if (newState === "running") {
        if (this.mode === "up") {
          // 正计时：pausedTime 存的是已走过的秒数
          const elapsed = this.pausedTime || 0;
          this._startTime = Date.now() - elapsed * 1000;
        } else {
          const remaining = this.pausedTime || this.seconds;
          this._endTime = Date.now() + remaining * 1000;
        }
        // 暂停恢复时按暂停时长平移随机触发点，避免一恢复就响
        if (this._randomRemainingMs != null) {
          this.nextRandomTrigger =
            Date.now() + Math.max(1000, this._randomRemainingMs);
          this._randomRemainingMs = null;
        }
        if (this.randomActive && !this.nextRandomTrigger) {
          this.scheduleNextRandom(
            this.mode === "down" ? this.pausedTime || this.seconds : null
          );
        }
        this.requestWakeLock();
        this.intervalId = setInterval(() => {
          const now = Date.now();
          if (this.mode === "up") {
            this.seconds = Math.max(
              0,
              Math.floor((now - this._startTime) / 1000)
            );
          } else {
            const left = Math.ceil((this._endTime - now) / 1000);
            if (left <= 0) {
              this.seconds = 0;
              this.finish(true);
              return;
            }
            this.seconds = left;
          }
          this.updateTray();
          if (
            this.randomActive &&
            this.nextRandomTrigger &&
            now >= this.nextRandomTrigger
          ) {
            this.triggerRandomAlert();
            this.scheduleNextRandom(
              this.mode === "down" ? this.seconds : null
            );
          }
        }, 1000);
        // 全新会话才调用 API 开始专注；暂停恢复只需重启心跳
        if (this._freshStart) {
          // 每段专注一个 live 标识（登录后会用后端 session_id 覆盖），供接力去重
          this._liveSessionId =
            "s-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
          if (this.isLoggedIn) this.apiStartFocus();
        } else if (this.isLoggedIn && this.currentSessionId) {
          this.startPing();
        }
        this._freshStart = false;
        // 专注进行中：对外广播 live 快照，供其他设备接力
        this.startHandoffPublish();
      } else {
        // 暂停或停止时，清除心跳与本地计时器，释放屏幕常亮
        this.stopPing();
        clearInterval(this.intervalId);
        this.releaseWakeLock();
        setTrayTitle("");
        // 停止对外广播（暂停/结束都不再让其他设备接力）
        this.stopHandoffPublish();
        // 停止背景环境音（仅运行中提供），并复位开关
        if (this.bgmOn) this.bgmOn = false;
      }
    },
  },
  created() {
    this.initTags();

    // 如果已登录，获取用户信息
    if (this.isLoggedIn) {
      this.fetchUserInfo();
    }
  },
  mounted() {
    document.title = "Zeno · 专注计时器";
    this.updateContainerDim();
    window.addEventListener("resize", this.updateContainerDim);
    document.addEventListener("visibilitychange", this.handleVisibility);
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    window.addEventListener(AUTH_EXPIRED_EVENT, this.handleAuthExpired);
    this.checkPayCallback();
    this.initHandoff();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateContainerDim);
    document.removeEventListener("visibilitychange", this.handleVisibility);
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
    window.removeEventListener(AUTH_EXPIRED_EVENT, this.handleAuthExpired);
    clearInterval(this.intervalId);
    clearInterval(this._microInterval);
    this.releaseWakeLock();
    this.teardownHandoff();
    stopAmbient();
  },
  // 计时进行中导航离开（如在应用内点回官网）时确认，避免误丢正在跑的专注。
  beforeRouteLeave(to, from, next) {
    if (this.running) {
      const ok = window.confirm(
        this.lang === "en"
          ? "A focus session is running. Leave and end it?"
          : "正在专注中，确定离开并结束吗？"
      );
      if (!ok) return next(false);
    }
    next();
  },
  methods: {
    initTags() {
      this.tags = [...this.t.tags];
      // 把持久化的自定义标签合并回来，切语言/刷新后不丢失
      this.customTags.forEach((t) => {
        if (!this.tags.includes(t)) this.tags.push(t);
      });
      if (
        !this.customTags.includes(this.tag) &&
        !this.t.tags.includes(this.tag)
      ) {
        this.tag = this.t.tags[0];
      }
    },
    updateContainerDim() {
      if (this.$refs.cardRef) {
        this.containerDim = {
          w: this.$refs.cardRef.clientWidth,
          h: this.$refs.cardRef.clientHeight,
        };
        // 读取真实生效的字体族（计时数字继承自卡片），让宽度测量与实际渲染一致。
        const fam = getComputedStyle(this.$refs.cardRef).fontFamily;
        if (fam) this.timerFontFamily = fam;
      }
    },
    // 把当前倒计时写进 mac 菜单栏（任务栏）。非桌面壳为 no-op。
    updateTray() {
      if (this.platform !== "desktop") return;
      const icon = this.isProtocolRest ? "☕" : "▶";
      setTrayTitle(`${icon} ${this.fmt(this.seconds)}`);
    },
    fmt(s) {
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
    },
    isSameDay(d1, d2) {
      return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
      );
    },
    warmUpAudio() {
      // 在用户手势内解锁 Web Audio 上下文：之后几分钟无手势上下文的
      // 心流提示音才能正常发声，不被移动端自动播放策略拦截。
      unlockAudio();
    },
    playSound(soundKey) {
      if (soundKey === "mute") return;
      const key = SOUNDS[soundKey] ? soundKey : "chime";
      playChime(key, 0.6);
    },
    // 计算下一次心流提示音的触发时刻；倒计时模式下避开最后 10 秒
    scheduleNextRandom(remainingSecs = null) {
      const minSec = Math.max(1, this.randomMin) * 60;
      const maxSec = Math.max(minSec, this.randomMax * 60);
      const delay =
        Math.floor(Math.random() * (maxSec - minSec + 1)) + minSec;
      if (remainingSecs != null) {
        const safe = Math.min(delay, remainingSecs - 10);
        this.nextRandomTrigger = safe > 0 ? Date.now() + safe * 1000 : null;
      } else {
        this.nextRandomTrigger = Date.now() + delay * 1000;
      }
    },
    triggerRandomAlert() {
      this.playSound("ping");
      if (this.vibrationOn && "vibrate" in navigator)
        navigator.vibrate([100, 50, 100]);
      nativeNotify("Zeno", this.t.neuro_toast_title || "闭眼微休息");
      this.microRest = { active: true, count: 10 };
      clearInterval(this._microInterval);
      this._microInterval = setInterval(() => {
        if (this.microRest.count <= 1) {
          this.endMicroRest();
        } else {
          this.microRest.count--;
        }
      }, 1000);
    },
    endMicroRest() {
      clearInterval(this._microInterval);
      if (!this.microRest.active) return;
      this.microRest = { active: false, count: 10 };
      // 轻提示音告知睁眼，回到专注
      this.playSound("ping");
    },
    applyProfile(key) {
      const p = PROFILES[key];
      if (!p) return;
      this.focusProfile = key;
      this.randomEnabled = p.random;
      this.randomMin = p.min;
      this.randomMax = p.max;
      this.protocolEnabled = p.protocol;
    },
    start(customDuration = null) {
      this.warmUpAudio();
      // 在用户手势内请求一次通知权限，否则后续微休息/完成通知在浏览器端永远发不出。
      ensureNotifyPermission();
      let startSecs = this.seconds;
      if (customDuration !== null) {
        startSecs = customDuration;
        this.seconds = customDuration;
      }
      // 启用 90/20 循环时，若用户本轮未显式选过时长，手动开始的首个专注段对齐到配置的专注时长，
      // 否则功能名为 90/20，首段却跑默认 25 分钟，与命名不符。
      // 但用户一旦在选择器里挑了时长（userPickedDuration），必须尊重其选择，不能强制覆盖为 90 分钟。
      if (
        customDuration === null &&
        !this.userPickedDuration &&
        this.protocolEnabled &&
        this.mode === "down" &&
        this.protocolPhase === "focus" &&
        this.timerState === "idle"
      ) {
        startSecs = Math.max(1, this.protocolFocusMin) * 60;
        this.seconds = startSecs;
      }
      if (startSecs === 0 && this.mode !== "up") return;
      const fresh = this.timerState === "idle" || customDuration !== null;
      if (fresh) {
        this.initialTime = startSecs;
        this.pausedTime = this.mode === "up" ? 0 : startSecs;
      }
      this._freshStart = fresh;
      this.timerState = "running";
      this.closeAllPanels();
    },
    pause() {
      // 记录随机触发点剩余毫秒，恢复时平移，避免暂停期间“欠下”的铃声立刻响起
      if (this.nextRandomTrigger) {
        this._randomRemainingMs = this.nextRandomTrigger - Date.now();
      }
      this.timerState = "paused";
      this.pausedTime = this.seconds;
    },
    isRestTag(tag) {
      return REST_TAGS.includes(tag);
    },
    flashCongrats(text) {
      this.congratsText = text;
      this.showCongrats = true;
      // 窗口可能被最小化/置后，用系统通知补一条（桌面壳走原生通知中心）。
      nativeNotify("Zeno", text);
      clearTimeout(this._congratsTimer);
      this._congratsTimer = setTimeout(() => {
        this.showCongrats = false;
      }, 2000);
    },
    async finish(natural = false) {
      this.endMicroRest();
      const wasRestPhase = this.isProtocolRest;
      this.timerState = "idle";
      this.pausedTime = null;
      this.nextRandomTrigger = null;
      this._randomRemainingMs = null;
      this.playSound(this.sound);
      // 休息类标签 / 90分钟循环的休息段不计入专注统计
      let completedMinutes = 0;
      if (!this.isRestTag(this.tag) && !wasRestPhase) {
        const recordDuration =
          this.mode === "up"
            ? Math.ceil(this.seconds / 60)
            : this.seconds === 0
            ? Math.ceil(this.initialTime / 60)
            : Math.ceil((this.initialTime - this.seconds) / 60);
        completedMinutes = recordDuration;
        if (recordDuration > 0) {
          const newItem = {
            id: Date.now(),
            date: new Date().toISOString(),
            duration: recordDuration,
            tag: this.tag,
          };
          this.history.unshift(newItem);
        }
      }

      // 调用 API 结束专注
      if (this.isLoggedIn && this.currentSessionId) {
        try {
          await finishFocus(this.currentSessionId);
        } catch (e) {
          console.error("Failed to finish focus session", e);
        }
        this.currentSessionId = null;
      }
      this.stopPing();

      // 90/20 宏循环：自然结束时在「专注 ⇄ 长休息」之间自动切换。
      // 必须等 watcher 先消化 idle（清掉旧 interval）再 start，
      // 否则同一 tick 内 running→idle→running 会被 Vue 异步 watcher 去重吞掉
      if (
        natural &&
        this.protocolEnabled &&
        this.mode === "down" &&
        !this.isRestTag(this.tag)
      ) {
        if (!wasRestPhase) {
          this.protocolPhase = "rest";
          this.flashCongrats(this.t.protocol_rest_start);
          if (this.vibrationOn && "vibrate" in navigator)
            navigator.vibrate([60, 40, 60]);
          this.$nextTick(() => this.start(this.protocolRestMin * 60));
        } else {
          this.protocolPhase = "focus";
          this.cycleCount++;
          this.flashCongrats(this.t.protocol_focus_start);
          this.$nextTick(() => this.start(this.protocolFocusMin * 60));
        }
        return;
      }

      // 手动结束则退出循环
      this.protocolPhase = "focus";
      this.cycleCount = 0;

      if (this.isRestTag(this.tag)) {
        this.resetToHome();
        return;
      }

      // 一轮专注结束：弹出完成引导（休息一下 / 再来一轮），
      // 避免倒计时归零后停在 0:00、开始按钮禁用、用户无从下手。
      if (this.vibrationOn && "vibrate" in navigator)
        navigator.vibrate([60, 40, 60]);
      nativeNotify("Zeno", this.t.congrats_title);
      this.completedMinutes = completedMinutes;
      this.sessionComplete = true;
    },
    endNow() {
      this.finish(false);
    },
    // —— 完成引导层的三个出口 ——
    // 再来一轮：按刚结束的模式重启（倒计时沿用上轮时长，正计时重新无限计时）。
    startAgain() {
      this.sessionComplete = false;
      if (this.mode === "up") {
        this.pickUnlimited();
      } else {
        this.seconds = this.initialTime || 25 * 60;
        this.start();
      }
    },
    // 休息一下：用「休息」标签起一段 5 分钟倒计时（不计入专注统计，结束后回到首页）。
    restAfterComplete() {
      this.sessionComplete = false;
      const restTag =
        this.t.tags && this.t.tags[1] ? this.t.tags[1] : this.tag;
      this.tag = restTag;
      this.mode = "down";
      this.seconds = 5 * 60;
      this.start();
    },
    // 返回：关闭引导并复位到首页默认态。
    dismissComplete() {
      this.sessionComplete = false;
      this.resetToHome();
    },
    async requestWakeLock() {
      // 保持屏幕常亮：方法要求 App 常驻前台，否则后台节流会让铃声不准
      try {
        if ("wakeLock" in navigator && !this._wakeLock) {
          this._wakeLock = await navigator.wakeLock.request("screen");
          this._wakeLock.addEventListener("release", () => {
            this._wakeLock = null;
          });
        }
      } catch (e) {
        // 不支持或被拒绝时静默降级
      }
    },
    releaseWakeLock() {
      if (this._wakeLock) {
        this._wakeLock.release().catch(() => {});
        this._wakeLock = null;
      }
    },
    handleVisibility() {
      if (document.visibilityState === "visible" && this.running) {
        this.requestWakeLock();
      }
    },
    // 计时进行中刷新/关闭标签页前弹浏览器原生确认，避免误丢正在跑的专注。
    handleBeforeUnload(e) {
      if (!this.running) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    },
    // 收到「登录态失效」广播（401）：复位为未登录/非会员，回到计时器主页并轻提示。
    handleAuthExpired() {
      if (!this.isLoggedIn && !this.isPro) return;
      this.isLoggedIn = false;
      this.isPro = false;
      this.currentSessionId = null;
      this.stopPing();
      this.closeAllPanels();
      if (this.currentView !== "timer") this.currentView = "timer";
      this.flashCongrats(this.lang === "en" ? "Session expired, please log in again" : "登录已过期，请重新登录");
    },
    resetToHome() {
      this.seconds = 25 * 60;
      this.userPickedDuration = false;
      this.tag = this.t.tags[0];
      this.mode = "down";
      this.timerState = "idle";
      this.pausedTime = null;
      this.showCongrats = false;
    },
    togglePanel(n) {
      if (this.panels[n]) {
        this.panels[n] = false;
      } else {
        this.closeAllPanels();
        this.panels[n] = true;
      }
      if (n !== "time") this.showCustomTime = false;
    },
    closeAllPanels() {
      Object.keys(this.panels).forEach((k) => (this.panels[k] = false));
      this.showCustomTime = false;
    },
    toggleProfileSection(s) {
      this.profileSections[s] = !this.profileSections[s];
      // 展开主题区时，若当前主题在「更多」里，自动展开更多区以露出选中项
      if (s === "theme" && this.profileSections.theme) {
        const active = this.themes[this.themeIdx];
        this.showMoreThemes =
          !!active && !FEATURED_THEME_IDS.includes(active.id);
      }
    },
    handleProfileClick() {
      // 个人中心含主题/音效/语言等纯本地个性化设置，游客也应可用；
      // 登录改由面板内头像区的「点击登录」触发，不再把整个面板挡在登录墙后。
      this.togglePanel("profile");
    },
    goLogin() {
      this.closeAllPanels();
      this.currentView = "login";
    },
    handleThemeSelect(idx) {
      if (THEMES[idx].isPro && !this.isPro) {
        this.currentView = "payment";
        return;
      }
      this.themeIdx = idx;
    },
    handleSoundSelect(key) {
      if (SOUNDS[key].isPro && !this.isPro) {
        this.currentView = "payment";
        return;
      }
      this.sound = key;
      this.playSound(key);
    },
    toggleRandom() {
      // 心流提示音是方法核心，对所有用户开放（高级主题/音效/云同步仍为 Pro）
      this.randomEnabled = !this.randomEnabled;
    },
    handleSyncClick() {
      if (!this.isPro) {
        this.currentView = "payment";
        return;
      }
      this.currentView = "sync";
    },
    handleLoginSuccess() {
      this.isLoggedIn = true;
      this.currentView = "timer";
      // 登录成功后刷新用户信息
      this.fetchUserInfo();
      // 登录后静默同步一次：把本地记录并入云端、拉回其他设备的记录（Pro 才真正落云）
      if (this.isPro) {
        this.doCloudSync().catch(() => {});
      }
      setTimeout(() => this.togglePanel("profile"), 300);
    },

    // 支付成功后处理
    handlePaymentSuccess() {
      this.fetchUserInfo();
      this.currentView = "vip_success";
    },
    applySeconds(sec) {
      this.mode = "down";
      this.seconds = sec;
      this.userPickedDuration = true;
      this.closeAllPanels();
    },
    pickUnlimited() {
      this.mode = "up";
      this.seconds = 0;
      this.closeAllPanels();
      this.start();
    },
    // 展开自定义面板时，立即把当前输入值同步到 seconds，
    // 这样即便用户不再编辑、不点 GO 直接开始，也按自定义时长跑
    openCustomTime() {
      this.showCustomTime = true;
      this.syncCustomTime();
    },
    // 把自定义输入折算成 seconds 并标记为用户已选时长，但不关闭面板（供边输入边同步用）
    syncCustomTime() {
      const mm = Math.max(0, Math.min(999, parseInt(this.customMin) || 0));
      const ss = Math.max(0, Math.min(59, parseInt(this.customSec) || 0));
      this.mode = "down";
      this.seconds = mm * 60 + ss;
      this.userPickedDuration = true;
    },
    applyCustomTime() {
      this.syncCustomTime();
      this.closeAllPanels();
    },
    applyTagChoice(t) {
      this.tag = t;
      if (!this.running && TAG_MAP[t]) {
        this.mode = "down";
        this.seconds = TAG_MAP[t];
      }
      this.closeAllPanels();
    },
    addCustomTag() {
      const t = (this.customTagInput || "").trim();
      if (t) {
        if (!this.tags.includes(t)) this.tags.push(t);
        if (!this.customTags.includes(t)) this.customTags.push(t);
        this.tag = t;
        this.closeAllPanels();
        this.customTagInput = "";
      }
    },
    removeCustomTag(t) {
      this.tags = this.tags.filter((x) => x !== t);
      this.customTags = this.customTags.filter((x) => x !== t);
      if (this.tag === t) this.tag = this.t.tags[0];
    },
    // 真实云同步：上传本地专注历史，服务端按 id 去重合并后回传全量，
    // 前端再并回本地。供 SyncView 通过 :on-sync 调用，也在登录后静默跑一次。
    async doCloudSync() {
      if (!this.isLoggedIn) throw new Error("not-logged-in");
      const local = this.history.map((h) => ({
        id: h.id,
        date: h.date,
        duration: h.duration,
        tag: h.tag,
      }));
      const res = await syncData(local);
      const cloud = (res && res.records) || [];
      const map = {};
      this.history.forEach((h) => {
        map[h.id] = h;
      });
      cloud.forEach((r) => {
        map[r.id] = {
          id: r.id,
          date: r.date,
          duration: r.duration,
          tag: r.tag,
        };
      });
      this.history = Object.values(map).sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      );
      this.lastSyncAt = Date.now();
      return { count: this.history.length, time: this.lastSyncAt };
    },
    async fetchUserInfo() {
      try {
        const data = await getUserInfo();
        // 根据返回更新状态 [cite: 77]
        // data.vip_type: 0非会员 1普通会员 2终身会员
        this.isPro = data.vip_type > 0;
        // 可以根据需要保存更多用户信息
      } catch (e) {
        if (e.message.includes("401")) {
          this.isLoggedIn = false; // Token 过期处理
          this.isPro = false; // 会员权益随登录态一起回收，避免未登录仍享 Pro
          localStorage.removeItem("zeno_token"); // 清掉失效令牌，后续请求不再携带
        }
      }
    },

    // 封装 API 调用
    async apiStartFocus() {
      try {
        const sessionId = await startFocus(); // [cite: 114]
        this.currentSessionId = sessionId; // [cite: 124] 返回的是 string
        this.startPing();
      } catch (e) {
        console.error("Failed to start focus session", e);
      }
    },

    startPing() {
      this.stopPing(); // 防止重复
      // 接口备注：计时器启动后需要每30s或1min上报一次 [cite: 150]
      this.pingInterval = setInterval(() => {
        if (this.currentSessionId) {
          pingFocus(this.currentSessionId).catch((e) =>
            console.error("Ping failed", e)
          );
        }
      }, 30000); // 30秒
    },

    stopPing() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    },
    // ============ 跨设备接力（Handoff） ============
    initHandoff() {
      // 后端通道：让接力跨越「每个平台端」（桌面/移动/Web 各自独立的进程与设备）。
      // 仅登录态启用（快照按用户维度共享）；端点未就绪时通道会自动熔断，不影响本地接力。
      const backend = {
        isEnabled: () => this.isLoggedIn,
        poll: () => getActiveFocus(),
        publish: (snap) => publishActiveFocus(snap),
        clear: (deviceId) => clearActiveFocus(deviceId),
        pollIntervalMs: 5000,
      };
      this.handoff = new Handoff(
        (snap) => this.onHandoffMessage(snap),
        { backend }
      );
      // 刚打开就看一眼是否已有另一台设备在专注（storage 事件只在变更时触发）
      const existing = this.handoff.readLatest();
      if (existing) this.onHandoffMessage(existing);
      // 每秒：刷新横幅剩余时间 + 淘汰已停止广播（超时）的接力提示
      this.handoffWatchTimer = setInterval(() => {
        this.handoffClock++;
        if (
          this.handoffOffer &&
          Date.now() - this.handoffOffer.updatedAt > 12000
        ) {
          this.handoffOffer = null;
        }
      }, 1000);
    },
    teardownHandoff() {
      this.stopHandoffPublish();
      clearInterval(this.handoffWatchTimer);
      if (this.handoff) {
        // 本设备正在广播则收回，避免留下「幽灵」会话误导其他设备
        if (this.running) this.handoff.clear();
        this.handoff.destroy();
        this.handoff = null;
      }
    },
    // 组装一份对外广播的 live 快照（接力方据此还原剩余时间与各项设置）。
    buildLiveSnapshot() {
      return {
        sessionId: this.currentSessionId || this._liveSessionId,
        platform: this.platform,
        mode: this.mode,
        tag: this.tag,
        seconds: this.seconds,
        initialTime: this.initialTime,
        startTime: this.mode === "up" ? this._startTime : null,
        endTime: this.mode === "down" ? this._endTime : null,
        protocolEnabled: this.protocolEnabled,
        protocolPhase: this.protocolPhase,
        protocolFocusMin: this.protocolFocusMin,
        protocolRestMin: this.protocolRestMin,
        cycleCount: this.cycleCount,
        randomEnabled: this.randomEnabled,
        randomMin: this.randomMin,
        randomMax: this.randomMax,
        focusProfile: this.focusProfile,
      };
    },
    startHandoffPublish() {
      if (!this.handoff) return;
      // 自己开始专注，任何待接力的提示都失效
      this.handoffOffer = null;
      const push = () => this.handoff.publish(this.buildLiveSnapshot());
      push(); // 立即广播一次
      clearInterval(this.handoffPublishTimer);
      // 每 5s 续广播，刷新 updatedAt（接力方据此判断「仍在进行中」）
      this.handoffPublishTimer = setInterval(push, 5000);
    },
    stopHandoffPublish() {
      clearInterval(this.handoffPublishTimer);
      this.handoffPublishTimer = null;
      // 仅当本设备不再处于运行态时才收回广播（被接力的让出路径单独处理）
      if (this.handoff && !this.running) this.handoff.clear();
    },
    onHandoffMessage(snap) {
      if (!snap) return;
      // 他设备停止广播：撤掉横幅
      if (snap.type === "clear") {
        this.handoffOffer = null;
        return;
      }
      const mySession = this.currentSessionId || this._liveSessionId;
      // 收到与「我正在跑的这条会话」同 id 的他设备快照 = 已被对方接力 → 静默让出
      if (this.running && mySession && snap.sessionId === mySession) {
        this.surrenderToHandoff();
        return;
      }
      // 我空闲时才提示接力，避免打断本机正在进行的专注
      if (!this.running && Date.now() - snap.updatedAt < 12000) {
        this.handoffOffer = snap;
      }
    },
    // 本设备已被其他设备接力：静默停止，但不结束后端会话（对方续传同一条会话）。
    surrenderToHandoff() {
      this.stopHandoffPublish();
      this.stopPing();
      clearInterval(this.intervalId);
      this.releaseWakeLock();
      setTrayTitle("");
      this.currentSessionId = null; // 交出所有权，避免后续 finish 调用 finishFocus
      this._liveSessionId = null;
      this.nextRandomTrigger = null;
      this._randomRemainingMs = null;
      this.endMicroRest();
      this.resetToHome(); // 把 timerState 置回 idle（watcher 已被上面手动清理过，clear 按设备守卫不会误删对方广播）
      this.flashCongrats(this.t.handoff_surrendered);
    },
    // 在本设备接管对方的专注：续传同一条会话，恢复剩余时间与设置后继续运行。
    adoptHandoff() {
      const snap = this.handoffOffer;
      if (!snap) return;
      this.handoffOffer = null;
      this.warmUpAudio();

      // 接管会话身份：登录态下沿用对方 session_id 续传心跳（不再 StartFocus 开新会话）
      this._liveSessionId = snap.sessionId;
      this.currentSessionId = this.isLoggedIn ? snap.sessionId : null;

      this.mode = snap.mode || "down";
      this.tag = snap.tag || this.tag;
      this.protocolEnabled = !!snap.protocolEnabled;
      this.protocolPhase = snap.protocolPhase || "focus";
      if (snap.protocolFocusMin) this.protocolFocusMin = snap.protocolFocusMin;
      if (snap.protocolRestMin) this.protocolRestMin = snap.protocolRestMin;
      this.cycleCount = snap.cycleCount || 0;
      this.randomEnabled = !!snap.randomEnabled;
      if (snap.randomMin) this.randomMin = snap.randomMin;
      if (snap.randomMax) this.randomMax = snap.randomMax;
      if (snap.focusProfile) this.focusProfile = snap.focusProfile;
      this.initialTime = snap.initialTime || snap.seconds || 0;

      const now = Date.now();
      if (this.mode === "up") {
        const elapsed = snap.startTime
          ? Math.max(0, Math.floor((now - snap.startTime) / 1000))
          : snap.seconds || 0;
        this.seconds = elapsed;
        this.pausedTime = elapsed; // running watcher 用 pausedTime 还原 _startTime
      } else {
        const remaining = snap.endTime
          ? Math.ceil((snap.endTime - now) / 1000)
          : snap.seconds || 0;
        if (remaining <= 0) {
          this.flashCongrats(this.t.congrats_title);
          return;
        }
        this.seconds = remaining;
        this.pausedTime = remaining; // running watcher 用 pausedTime 还原 _endTime
      }

      // 关键：标记为「非全新开始」，watcher 不再 apiStartFocus（避免重复开会话），
      // 登录态下直接 startPing 续传对方的 session_id。
      this._freshStart = false;
      this.nextRandomTrigger = null;
      this._randomRemainingMs = null;
      this.timerState = "running";
      this.closeAllPanels();
      this.flashCongrats(this.t.handoff_adopted);
    },
    handleLogout() {
      // 1. 重置界面状态
      this.isLoggedIn = false;
      this.isPro = false; // 如果非会员可以保留，根据业务需求调整

      // 2. 清理本地存储 (localStorage)
      // 注意：data() 中的 isLoggedIn 会通过 watch 自动更新 localStorage['zeno_isLoggedIn']
      // 但我们需要手动清理 Token (上一轮接入 API 时约定的 Key)
      localStorage.removeItem("zeno_token");

      // 3. 关闭所有面板
      this.closeAllPanels();

      // 4. (可选) 如果有专注心跳，停止心跳上报
      if (this.stopPing) {
        this.stopPing();
      }
    },
    // 测试面板：清空全部本地状态并刷新，回到全新用户初始态。
    devReset() {
      if (!window.confirm("清空所有本地数据（登录/会员/主题/专注记录）并刷新？")) {
        return;
      }
      Object.keys(localStorage)
        .filter((k) => k.startsWith("zeno_"))
        .forEach((k) => localStorage.removeItem(k));
      location.reload();
    },
    checkPayCallback() {
      const { payStatus } = this.$route.query;
      if (payStatus) {
        // 清除 URL 参数，避免刷新再次触发
        this.$router.replace({ query: {} });
        if (payStatus === "success") {
          this.handlePaymentSuccess();
        } else if (payStatus === "failed") {
          this.showCongrats = true;
          this.congratsText = "支付失败";
          setTimeout(() => {
            this.showCongrats = false;
          }, 1500);
        }
      }
    },
  },
};
</script>
